import { sql } from "drizzle-orm";
import { check, date, foreignKey, integer, pgTable, text, timestamp, uniqueIndex, varchar } from "drizzle-orm/pg-core";
import { organisations, students, schoolPupils } from "./schemaBase.js";

/** Award receipts survive task edits/withdrawal. Source ids are provenance,
 * not cascading references to disposable task/review records. */
export const studentAwardRecords = pgTable("student_award_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: varchar("organisation_id").notNull(),
  studentId: varchar("student_id"),
  schoolPupilId: varchar("school_pupil_id"),
  awardKey: text("award_key").notNull(),
  instanceKey: text("instance_key").notNull(),
  title: text("title").notNull(),
  earnedAt: timestamp("earned_at", { withTimezone: true }).notNull().defaultNow(),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
  revocationReason: text("revocation_reason"),
  sourceDomain: text("source_domain").notNull(),
  sourceId: text("source_id"),
  awardedBySubject: text("awarded_by_subject"),
  reason: text("reason"),
}, t => [
  foreignKey({ name: "student_award_records_org_fk", columns: [t.organisationId], foreignColumns: [organisations.id] }).onDelete("restrict"),
  foreignKey({ name: "student_award_records_student_fk", columns: [t.studentId], foreignColumns: [students.id] }).onDelete("cascade"),
  foreignKey({ name: "student_award_records_pupil_fk", columns: [t.schoolPupilId], foreignColumns: [schoolPupils.id] }).onDelete("cascade"),
  check("student_awards_recipient", sql`num_nonnulls(${t.studentId}, ${t.schoolPupilId}) = 1`),
  check("student_awards_domain", sql`(${t.sourceDomain} in ('practice','private') and ${t.studentId} is not null) or (${t.sourceDomain} = 'school' and ${t.schoolPupilId} is not null)`),
  uniqueIndex("student_awards_student_unique").on(t.organisationId, t.studentId, t.awardKey, t.instanceKey),
  uniqueIndex("student_awards_pupil_unique").on(t.organisationId, t.schoolPupilId, t.awardKey, t.instanceKey),
]);

export const studentAwardProgress = pgTable("student_award_progress", {
  studentId: varchar("student_id").primaryKey(),
  organisationId: varchar("organisation_id").notNull(),
  longestStreak: integer("longest_streak").notNull().default(0),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  foreignKey({ name: "student_award_progress_org_fk", columns: [t.organisationId], foreignColumns: [organisations.id] }).onDelete("restrict"),
  foreignKey({ name: "student_award_progress_student_fk", columns: [t.studentId], foreignColumns: [students.id] }).onDelete("cascade"),
  check("student_awards_streak_positive", sql`${t.longestStreak} >= 0`),
]);

/** Targets take effect at a week boundary; changing one cannot rewrite an
 * earlier week's target. Sixty minutes is the default before the first row. */
export const studentAwardWeeklyTargets = pgTable("student_award_weekly_targets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: varchar("organisation_id").notNull(),
  studentId: varchar("student_id").notNull(),
  startsOn: date("starts_on").notNull(),
  minutes: integer("minutes").notNull(),
}, t => [
  foreignKey({ name: "student_award_targets_org_fk", columns: [t.organisationId], foreignColumns: [organisations.id] }).onDelete("restrict"),
  foreignKey({ name: "student_award_targets_student_fk", columns: [t.studentId], foreignColumns: [students.id] }).onDelete("cascade"),
  check("student_awards_target_minutes", sql`${t.minutes} between 1 and 3360`),
  uniqueIndex("student_awards_target_week_unique").on(t.organisationId, t.studentId, t.startsOn),
]);
